/**
 * Common validation utilities for UI components
 */

import type { RangeValue, ParameterValue } from "@/types/simulation";
import { isRangeValue } from "@/types/simulation";

/**
 * Validation result for field validation
 */
export interface FieldValidationResult {
  fieldsWithOperatorButNoValue: string[];
  fieldsWithIncompleteBetween: string[];
  fieldsWithInvalidRange: string[];
  fieldsWithEmptyInValues: string[];
}

/**
 * Check if a value is empty (null, undefined, or empty string)
 */
export const isEmpty = (value: unknown): boolean => {
  return value === null || value === undefined || value === "";
};

/**
 * Check if a value is not empty
 */
export const isNotEmpty = (value: unknown): boolean => {
  return !isEmpty(value);
};

/**
 * Validate a string is not empty or whitespace only
 */
export const isValidString = (value: unknown): boolean => {
  return typeof value === "string" && value.trim().length > 0;
};

/**
 * Validate a range value has both from and to values
 */
export const isCompleteRange = (value: RangeValue): boolean => {
  const fromVal = value.from ?? value.min;
  const toVal = value.to ?? value.max;
  return isNotEmpty(fromVal) && isNotEmpty(toVal);
};

/**
 * Validate a range value has at least one value (from or to)
 */
export const hasAnyRangeValue = (value: RangeValue): boolean => {
  const fromVal = value.from ?? value.min;
  const toVal = value.to ?? value.max;
  return isNotEmpty(fromVal) || isNotEmpty(toVal);
};

/**
 * Validate a range value has valid order (from <= to)
 */
export const isValidRangeOrder = (value: RangeValue): boolean => {
  const fromVal = value.from ?? value.min;
  const toVal = value.to ?? value.max;
  if (isEmpty(fromVal) || isEmpty(toVal)) return true;
  return Number(fromVal) <= Number(toVal);
};

/**
 * Validate an array has at least one non-empty value
 */
export const hasNonEmptyArrayValues = (
  value: (string | number | null | undefined)[],
): boolean => {
  return value.some((v) => isNotEmpty(v));
};

/**
 * Filter out empty values from an array
 */
export const filterEmptyValues = <T>(
  values: (T | null | undefined | string)[],
): T[] => {
  return values.filter((v) => isNotEmpty(v)) as T[];
};

/**
 * Validate BETWEEN operator value
 * Returns: { valid: boolean, errorType: 'missing' | 'incomplete' | 'invalid_order' | null }
 */
export const validateBetweenValue = (
  value: ParameterValue,
): {
  valid: boolean;
  errorType: "missing" | "incomplete" | "invalid_order" | null;
} => {
  if (!isRangeValue(value)) {
    return { valid: false, errorType: "missing" };
  }

  const fromVal = value.from ?? value.min;
  const toVal = value.to ?? value.max;

  // Both values missing
  if (isEmpty(fromVal) && isEmpty(toVal)) {
    return { valid: false, errorType: "missing" };
  }

  // Only one value provided (incomplete)
  if (isEmpty(fromVal) || isEmpty(toVal)) {
    return { valid: false, errorType: "incomplete" };
  }

  // From > To (invalid order)
  if (Number(fromVal) > Number(toVal)) {
    return { valid: false, errorType: "invalid_order" };
  }

  return { valid: true, errorType: null };
};

/**
 * Validate IN operator value
 * Returns: { valid: boolean, errorType: 'missing' | 'empty_array' | null }
 */
export const validateInValue = (
  value: ParameterValue,
): { valid: boolean; errorType: "missing" | "empty_array" | null } => {
  if (Array.isArray(value)) {
    const nonEmptyValues = value.filter((v) => isNotEmpty(v));
    if (nonEmptyValues.length === 0) {
      return { valid: false, errorType: "empty_array" };
    }
    return { valid: true, errorType: null };
  }

  if (isEmpty(value)) {
    return { valid: false, errorType: "missing" };
  }

  return { valid: true, errorType: null };
};

/**
 * Validate standard operator value (=, !=, <, >, <=, >=, LIKE, etc.)
 */
export const validateStandardValue = (
  value: ParameterValue,
): { valid: boolean; errorType: "missing" | null } => {
  if (isEmpty(value)) {
    return { valid: false, errorType: "missing" };
  }
  return { valid: true, errorType: null };
};

/**
 * Validate a field based on its operator and value
 */
export const validateFieldValue = (
  operator: string | undefined,
  value: ParameterValue,
): { valid: boolean; errorType: string | null } => {
  if (!operator) {
    return { valid: true, errorType: null };
  }

  const normalizedOperator = operator.toLowerCase();

  if (normalizedOperator === "between") {
    return validateBetweenValue(value);
  }

  if (normalizedOperator === "in") {
    return validateInValue(value);
  }

  return validateStandardValue(value);
};

/**
 * Validate all fields and return categorized validation errors
 */
export const validateFields = (
  fields: Array<{ fieldName?: string }>,
  operatorValues: Record<string, string>,
  parameterValues: Record<string, ParameterValue>,
): FieldValidationResult => {
  const result: FieldValidationResult = {
    fieldsWithOperatorButNoValue: [],
    fieldsWithIncompleteBetween: [],
    fieldsWithInvalidRange: [],
    fieldsWithEmptyInValues: [],
  };

  fields.forEach((field) => {
    const fieldName = field.fieldName ?? "";
    if (!fieldName) return;

    const operator = operatorValues[fieldName];
    const value = parameterValues[fieldName];

    if (!operator) return;

    const normalizedOperator = operator.toLowerCase();

    // BETWEEN operator validation
    if (normalizedOperator === "between") {
      const validation = validateBetweenValue(value);
      if (!validation.valid) {
        switch (validation.errorType) {
          case "missing":
            result.fieldsWithOperatorButNoValue.push(fieldName);
            break;
          case "incomplete":
            result.fieldsWithIncompleteBetween.push(fieldName);
            break;
          case "invalid_order":
            result.fieldsWithInvalidRange.push(fieldName);
            break;
        }
      }
      return;
    }

    // IN operator validation
    if (normalizedOperator === "in") {
      const validation = validateInValue(value);
      if (!validation.valid) {
        switch (validation.errorType) {
          case "missing":
            result.fieldsWithOperatorButNoValue.push(fieldName);
            break;
          case "empty_array":
            result.fieldsWithEmptyInValues.push(fieldName);
            break;
        }
      }
      return;
    }

    // Standard operator validation
    const validation = validateStandardValue(value);
    if (!validation.valid) {
      result.fieldsWithOperatorButNoValue.push(fieldName);
    }
  });

  return result;
};

/**
 * Check if validation result has any errors
 */
export const hasValidationErrors = (result: FieldValidationResult): boolean => {
  return (
    result.fieldsWithOperatorButNoValue.length > 0 ||
    result.fieldsWithIncompleteBetween.length > 0 ||
    result.fieldsWithInvalidRange.length > 0 ||
    result.fieldsWithEmptyInValues.length > 0
  );
};

/**
 * Get the first validation error message from validation result
 */
export const getFirstValidationError = (
  result: FieldValidationResult,
): string | null => {
  if (result.fieldsWithOperatorButNoValue.length > 0) {
    const count = result.fieldsWithOperatorButNoValue.length;
    return `Please enter a value for the following parameter${
      count > 1 ? "s" : ""
    }: ${result.fieldsWithOperatorButNoValue.join(", ")}`;
  }

  if (result.fieldsWithIncompleteBetween.length > 0) {
    return `Please enter both "From" and "To" values for BETWEEN operator on: ${result.fieldsWithIncompleteBetween.join(", ")}`;
  }

  if (result.fieldsWithInvalidRange.length > 0) {
    return `Invalid range: "From" value must be less than or equal to "To" value for: ${result.fieldsWithInvalidRange.join(", ")}`;
  }

  if (result.fieldsWithEmptyInValues.length > 0) {
    return `Please enter at least one value for IN operator on: ${result.fieldsWithEmptyInValues.join(", ")}`;
  }

  return null;
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate numeric value is within range
 */
export const isWithinRange = (
  value: number,
  min?: number,
  max?: number,
): boolean => {
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
};

/**
 * Validate array has minimum length
 */
export const hasMinLength = (arr: unknown[], minLength: number): boolean => {
  return arr.length >= minLength;
};

/**
 * Validate string matches pattern
 */
export const matchesPattern = (value: string, pattern: RegExp): boolean => {
  return pattern.test(value);
};
